import produce from "immer";
import {
  difference,
  differenceBy,
  mapValues,
  some,
  union,
  unionBy,
  xor,
} from "lodash";
import { SAVED_FILTERS, SAVED_SORT, TASK_LIST_GROUPS } from "core/constants";
import {
  ActionType,
  AddBatchJobAction,
  AddFileUploadAttachments,
  AddRefetchListenerAction,
  AddUndoAction,
  AddValidationException,
  BulkSetFiltersAction,
  ClearFileUploadAttachments,
  ClearRefetchQueriesAction,
  ClearValidationException,
  CollapseGroupAction,
  DeselectTasksAction,
  ExpandGroupAction,
  FileUploadAttachments,
  RefetchQueriesAction,
  RemoveBatchJobAction,
  RemoveRefetchListenerAction,
  RemoveUndoAction,
  SelectTasksAction,
  SetFiltersAction,
  SetGroupByOptionsAction,
  SetSortOptionsAction,
  ToggleTaskAction,
  UnsetFiltersAction,
} from "tasks/actions";
import {
  diffPriorityFilters,
  diffStatusFilters,
  diffTomTagFilters,
  unionPriorityFilters,
  unionStatusFilters,
  unionTomTagFilters,
} from "tasks/helpers";
import {
  FilterClient,
  FilterStatus,
  FilterTriaged,
  TaskFilterInput,
  TaskSortInput,
} from "typings/globalGQLTypes";

const returnAttachmentKey = (item: FileUploadAttachments) =>
  `${item.attachableId}|${item.attachableType}:${item.files
    .map(f => f.name)
    .join(",")}`;
interface RefetchListenerType {
  shouldRefetch: boolean;
  queryNames: QueryNameType[];
}

export interface TasksState {
  batchJobs: string[];
  collapsedGroups: CollapsedGroup;
  filters: TaskFilterInput;
  groupBy: GroupBy;
  refetchListeners: { [uuid: string]: RefetchListenerType };
  selectedTasks: string[];
  sort: TaskSortInput[];
  undoComplete: {
    taskId: string;
    message: string;
  } | null;
  uploadAttachments: FileUploadAttachments[];
  validationExceptions: string[];
}
export type AllTasksActions =
  | SetGroupByOptionsAction
  | SetFiltersAction
  | BulkSetFiltersAction
  | UnsetFiltersAction
  | SetSortOptionsAction
  | AddUndoAction
  | RemoveUndoAction
  | ExpandGroupAction
  | CollapseGroupAction
  | SelectTasksAction
  | DeselectTasksAction
  | ToggleTaskAction
  | AddBatchJobAction
  | RemoveBatchJobAction
  | AddRefetchListenerAction
  | RemoveRefetchListenerAction
  | RefetchQueriesAction
  | ClearRefetchQueriesAction
  | AddValidationException
  | ClearValidationException
  | AddFileUploadAttachments
  | ClearFileUploadAttachments;

// below state is shared between tasks and schedule page
// need more investigation to make this cleaner in the future
export const sharedState = {
  assignee: [],
  buildingIds: [],
};

export const initialState: TasksState = {
  filters: {
    ...sharedState,
    city: null,
    cleaningRequestCategory: [],
    clientType: FilterClient.ops,
    dueAtEnd: null,
    dueAtStart: null,
    duringStay: null,
    occupancy: null,
    priority: null,
    status: [FilterStatus.incomplete],
    taskType: [],
    tomTags: [],
    triaged: FilterTriaged.triaged,
  },
  sort: JSON.parse(localStorage.getItem(SAVED_SORT) || "[]"),
  groupBy: "due-date",
  selectedTasks: [],
  batchJobs: [],
  collapsedGroups: {
    new: false,
    everything: false,
    pastdue: false,
    today: false,
    tomorrow: true,
    upcoming: true,
    ...JSON.parse(localStorage.getItem(TASK_LIST_GROUPS) || "{}"),
  },
  refetchListeners: {},
  undoComplete: null,
  uploadAttachments: [],
  validationExceptions: [],
};

export const savedFilterState = {
  ...initialState,
  filters: {
    ...initialState.filters,
    ...JSON.parse(localStorage.getItem(SAVED_FILTERS) || "{}"),
  },
};

const saveFilters = (filters: TaskFilterInput) => {
  localStorage.setItem(SAVED_FILTERS, JSON.stringify(filters));
};

const saveSort = (sort: TaskSortInput[]) => {
  localStorage.setItem(SAVED_SORT, JSON.stringify(sort));
};

export const tasksReducer = (
  state = {
    ...savedFilterState,
  } as TasksState,
  action: AllTasksActions,
) =>
  produce(state, draft => {
    switch (action.type) {
      case ActionType.SetFilters: {
        if (action.payload) {
          draft.filters = {
            ...draft.filters,
            ...action.payload,
            ...unionPriorityFilters({
              globalFilters: draft.filters,
              localFilters: action.payload,
            }),
            ...unionStatusFilters({
              globalFilters: draft.filters,
              localFilters: action.payload,
            }),
            ...unionTomTagFilters({
              globalFilters: draft.filters,
              localFilters: action.payload,
            }),
          };
          saveFilters(draft.filters);
        }
        return draft;
      }
      case ActionType.UnsetFilters: {
        if (action.payload) {
          draft.filters = {
            ...draft.filters,
            ...action.payload,
            duringStay: action.payload.duringStay
              ? null
              : draft.filters.duringStay,
            ...diffPriorityFilters({
              globalFilters: draft.filters,
              localFilters: action.payload,
            }),
            ...diffStatusFilters({
              globalFilters: draft.filters,
              localFilters: action.payload,
            }),
            ...diffTomTagFilters({
              globalFilters: draft.filters,
              localFilters: action.payload,
            }),
          };
          saveFilters(draft.filters);
        }
        return draft;
      }

      case ActionType.BulkSetFilters: {
        if (action.payload) {
          draft.filters = {
            ...initialState.filters,
            ...action.payload,
          };
          saveFilters(draft.filters);
        }
        return draft;
      }

      case ActionType.SetSortOptions: {
        draft.sort = action.payload || initialState.sort;
        saveSort(draft.sort);
        return draft;
      }

      case ActionType.SetGroupByOptions: {
        draft.groupBy = action.payload || initialState.groupBy;
        return draft;
      }

      case ActionType.AddUndo: {
        draft.undoComplete = action.payload;
        return draft;
      }

      case ActionType.RemoveUndo: {
        draft.undoComplete = null;
        return draft;
      }

      case ActionType.ExpandGroup: {
        draft.collapsedGroups[action.payload.groupId] = false;
        localStorage.setItem(
          TASK_LIST_GROUPS,
          JSON.stringify(draft.collapsedGroups),
        );
        return draft;
      }

      case ActionType.CollapseGroup: {
        draft.collapsedGroups[action.payload.groupId] = true;
        localStorage.setItem(
          TASK_LIST_GROUPS,
          JSON.stringify(draft.collapsedGroups),
        );
        return draft;
      }

      case ActionType.SelectTasksAction: {
        const taskIds = action.payload.taskIds;
        draft.selectedTasks = union(draft.selectedTasks, taskIds);
        return draft;
      }

      case ActionType.DeselectTasksAction: {
        const taskIds = action.payload.taskIds;
        draft.selectedTasks = difference(draft.selectedTasks, taskIds);
        return draft;
      }

      case ActionType.ToggleTaskAction: {
        const {
          payload: { taskId },
        } = action;
        draft.selectedTasks = xor(draft.selectedTasks, [taskId]);
        return draft;
      }

      case ActionType.AddBatchJobAction: {
        const {
          payload: { jobId },
        } = action;
        draft.batchJobs.push(jobId);
        return draft;
      }

      case ActionType.RemoveBatchJobAction: {
        const {
          payload: { jobId },
        } = action;
        draft.batchJobs = difference(draft.batchJobs, [jobId]);
        return draft;
      }

      case ActionType.AddRefetchListenerAction: {
        const {
          payload: { uuid, queryNames },
        } = action;
        draft.refetchListeners[uuid] = {
          shouldRefetch: false,
          queryNames,
        };
        return draft;
      }

      case ActionType.RemoveRefetchListenerAction: {
        const {
          payload: { uuid },
        } = action;
        delete draft.refetchListeners[uuid];
        return draft;
      }

      case ActionType.RefetchQueriesAction: {
        const {
          payload: { queryNames },
        } = action;

        draft.refetchListeners = {
          ...(draft.refetchListeners || {}),
          ...mapValues(draft.refetchListeners, refetchListener => ({
            ...refetchListener,
            shouldRefetch: some(refetchListener.queryNames, name =>
              queryNames.includes(name),
            ),
          })),
        };

        return draft;
      }

      case ActionType.ClearRefetchQueriesAction: {
        const {
          payload: { uuid },
        } = action;
        const currentListener = draft.refetchListeners[uuid];

        draft.refetchListeners[uuid] = {
          ...currentListener,
          shouldRefetch: false,
        };

        return draft;
      }

      case ActionType.AddValidationException: {
        draft.validationExceptions = union(draft.validationExceptions, [
          action.payload.exception,
        ]);

        return draft;
      }

      case ActionType.ClearValidationException: {
        draft.validationExceptions = difference(draft.validationExceptions, [
          action.payload.exception,
        ]);
        return draft;
      }

      case ActionType.AddFileUploadAttachments: {
        draft.uploadAttachments = unionBy(
          draft.uploadAttachments,
          [action.payload.uploadAttachments],
          item => returnAttachmentKey(item),
        );
        return draft;
      }

      case ActionType.ClearFileUploadAttachments: {
        draft.uploadAttachments = differenceBy(
          draft.uploadAttachments,
          [action.payload.uploadAttachments],
          item => returnAttachmentKey(item),
        );
        return draft;
      }

      default:
        return draft;
    }
  });
